/**
 * ChipInput — reusable tag/chip input with keyboard support.
 * Press Enter to add a chip, click X to remove.
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { FieldHint } from '@/components/field-hint'

export type ChipInputProps = {
  readonly label: string
  readonly placeholder: string
  readonly chips: readonly string[]
  readonly onChange: (chips: readonly string[]) => void
  readonly disabled?: boolean
  readonly hint?: string
}

export function ChipInput({ label, placeholder, chips, onChange, disabled, hint }: ChipInputProps) {
  const [inputVal, setInputVal] = useState('')

  function commit() {
    const trimmed = inputVal.trim()
    if (!trimmed) return
    // Auto-split on common delimiters so a paste like "saas & enterprise"
    // splits into two chips. We intentionally do NOT split on commas here,
    // commas are part of normal multi-word phrases and the user is told
    // to press Enter to lock each entry instead.
    const parts = trimmed
      .split(/\s*[&/]\s*|\s+and\s+/i)
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && !chips.includes(p))
    if (parts.length === 0) return
    onChange([...chips, ...parts])
    setInputVal('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    }
  }

  function handleBlur() {
    // Lock the in-flight typed value as a chip when focus leaves the field
    // so the user doesn't lose what they typed by clicking Search before
    // pressing Enter.
    commit()
  }

  function removeChip(chip: string) {
    onChange(chips.filter((c) => c !== chip))
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label>{label}</Label>
        {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
      </div>
      <div className="flex flex-wrap gap-1.5 rounded-md border border-input bg-transparent p-2 min-h-[40px]">
        {chips.map((chip) => (
          <Badge key={chip} variant="secondary" className="gap-1 pr-1">
            {chip}
            <button
              type="button"
              onClick={() => removeChip(chip)}
              disabled={disabled}
              className="rounded-full hover:bg-muted-foreground/20 p-0.5"
              aria-label={`Remove ${chip}`}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          className="flex-1 min-w-[120px] bg-transparent text-sm placeholder:text-subtle-foreground focus:outline-none disabled:cursor-not-allowed"
          placeholder={placeholder}
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </div>
      <p className="text-xs text-muted-foreground">Press Enter after each one to lock it in</p>
    </div>
  )
}
